import {globalUser} from "./globals.js";
import {
    championshipPrestigeXps,
    commaSeparate, getArrayMaxByProperty,
    getRandomChoice,
    getRandomInt,
    getRandomUniform,
    goalProperties,
    myround, objectInArray
} from "./helpers.js";
import {getCompanyRosterByType} from "./companies.js";
import {getCompanyChampionships, getCompanyGimmickMatches} from "../models/company.js";
import {Objectives, Properties} from "../models/models.js";
import {addObjectiveProperties, Objective} from "../models/objective.js";
import {getChampionshipDisplayName} from "../ui/ui-helpers.js";
import {addPropertyGimmickMatches, addPropertySuperstars, Property} from "../models/property.js";
import {getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {getRankFromPopularity} from "./superstars.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";

async function generateCompanyObjectives(company, game) {  // generate_objectives
    let mainRoster = await getCompanyRosterByType(company, 'main');
    let gimmickMatches = await getCompanyGimmickMatches(company);
    let championships = await getCompanyChampionships(company);
    let properties = JSON.parse(JSON.stringify(goalProperties));

    // Add championship prestige property name for paid users
    if (globalUser.paid) {
        properties['company'].push('championship prestige');
    }

    // Remove award property names if no competitors present
    if (!game.competitorsPresent) {
        properties['company'] = properties['company']
            .filter(x => !['win awards', 'win moty', 'win eoty', 'win woty', 'win gmoty'].includes(x));
    }

    // Select random property names from each tag
    let selectedProperties = {};
    for (let x of ['company', 'superstar', 'rating']) {
        selectedProperties[x] = getRandomChoice(properties[x]);
    }

    // Create properties and objectives
    let objectives = [];
    for (let [tag, name] of Object.entries(selectedProperties)) {
        if (company.storyName === 'tutorial' && tag === 'superstar') {
            name = 'main eventer';
        }
        let objectiveProperty = await createProperty(name, tag, mainRoster, game.year, gimmickMatches, championships,
            12, company.storyName);
        let newObjective = await createObjective(objectiveProperty);
        await addObjectiveProperties(newObjective, [objectiveProperty]);
        objectives.push(newObjective);
    }

    return objectives;
}

async function generateCompanySingleObjective(roster, gimmickMatches, year, tag, championships, game, nameException = null,
                                              monthsRemaining = 12) {  // generate_objective
    let properties = JSON.parse(JSON.stringify(goalProperties));

    // Add championship prestige goal for paid users
    if (globalUser.paid) {
        properties['company'].push('championship prestige')
    }

    // Remove award objectives if no competitors present or awards passed
    if (!game.competitorsPresent || monthsRemaining <= 2) {
        properties['company'] = properties['company']
            .filter(x => !['win awards', 'win moty', 'win eoty', 'win woty', 'win gmoty'].includes(x));
    }

    // Select property
    let name = getRandomChoice(properties[tag]);
    if (nameException) {
        // Don't select name exception
        while (name === nameException) {
            name = getRandomChoice(properties[tag]);
        }
    }

    // Create property and objective
    const objectiveProperty = await createProperty(name, tag, roster, year, gimmickMatches, championships, monthsRemaining);
    const newObjective = await createObjective(objectiveProperty);
    await addObjectiveProperties(newObjective, [objectiveProperty]);

    return newObjective;
}

async function createObjective(objectiveProperty) {  // create_objective
    let properties = {
        'events': {'name': `Hold {0} {1}* or higher {2}`, 'help': 'Only monthly Events count.'},
        'matches': {'name': `Hold {0} {1}* or higher {2}`, 'help': 'Only Matches at monthly Events count'},
        'holy shit moments': {'name': 'Create {0} Holy Shit {1}',
            'help': 'Holy shit moments occur when cashing in a Cash-in Contract, through shocking Turns and have a ' +
                'chance of occurring during Gimmick Matches.'},
        'main eventers signed': {'name': 'Sign {0} Main Event Talent', 'help': ''},
        'win awards': {'name': 'Win {0} Year End {1} (no bad awards!)',
            'help': 'Win any of the \'Best\' Awards. No bad awards!'},
        'win moty': {'name': 'Win {0} Match of the Year Award', 'help': ''},
        'win eoty': {'name': 'Win {0} Event of the Year Award', 'help': ''},
        'win gmoty': {'name': 'Win {0} Craziest Match of the Year Award', 'help': ''},
        'win woty': {'name': 'Win {0} Wrestler of the Year Award',
            'help': 'Wrestler of the year goes to the Superstar with the best average Match rating.'},
        'new championship': {'name': 'Create {0} new Championship', 'help': ''},
        'championship prestige': {'name': 'Raise the prestige of the {0} to {1}', 'help': ''},
        'new tag team': {'name': 'Create a Tag Team of {0} and make them Upper Midcarders', 'help': ''},
        'new trio': {'name': 'Create a Trio of {0} and make them Main Eventers', 'help': ''},
        'win battle royal': {'name': 'Have {0} win a major Battle Royal', 'help': ''},
        'win championship contract': {'name': 'Have {0} win a Cash-in Contract', 'help': ''},
        'face of the company': {'name': 'Make {0} a Face of the Company',
            'help': 'Start by bringing a superstar to 1000 popularity and then keep it above 1000 for at least ' +
                'six monthly Events.'},
        'main eventer': {'name': 'Make {0} a Main Eventer', 'help': ''},
        'get over': {'name': 'Get {0} Over with the Fans', 'help': 'Get Overness to 80%'},
        'gimmick matches': {'name': 'Hold {0} {1}* or higher {2} {3}',
            'help': 'Only Matches at monthly Events count.'}
    };

    // Get name
    let name = null;
    if (objectiveProperty.tag === 'superstar') {
        name = properties[objectiveProperty.name]['name'].format(objectiveProperty.superstarName);
    } else if (objectiveProperty.tag === 'rating') {
        const rating = String(objectiveProperty.rating);
        let noun;
        if (objectiveProperty.name === 'events') {
            noun = objectiveProperty.activationValue === 1 ? 'Event' : 'Events';
        } else if (objectiveProperty.name === 'matches' || objectiveProperty.name === 'gimmick matches') {
            noun = objectiveProperty.activationValue === 1 ? 'Match' : 'Matches';
        }
        if (objectiveProperty.name === 'gimmick matches') {
            name = properties[objectiveProperty.name]['name'].format(objectiveProperty.activationValue, rating,
                objectiveProperty.gimmickMatchName, noun);
        } else {
            name = properties[objectiveProperty.name]['name'].format(objectiveProperty.activationValue, rating, noun);
        }
    } else if (objectiveProperty.tag === 'company') {
        if (objectiveProperty.name === 'championship prestige') {
            name = properties[objectiveProperty.name]['name'].format(objectiveProperty.championshipName,
                getChampionshipPrestigeIconHtml(objectiveProperty.activationText));
        }
        else if (objectiveProperty.name === 'holy shit moments') {
            let noun = objectiveProperty.activationValue === 1 ? 'Moment' : 'Moments';
            name = properties[objectiveProperty.name]['name'].format(String(objectiveProperty.activationValue), noun);
        }
        else if (objectiveProperty.name === 'win awards') {
            let noun = objectiveProperty.activationValue === 1 ? 'Award' : 'Awards';
            name = properties[objectiveProperty.name]['name'].format(String(objectiveProperty.activationValue), noun);
        } else {
            name = properties[objectiveProperty.name]['name'].format(String(objectiveProperty.activationValue));
        }
    }

    // Create objective
    let newObjective = Objective(name);
    await Objectives.insert(newObjective);

    // Add help text
    let helpText = properties[objectiveProperty.name]['help'];
    if (helpText) {
        newObjective.helpText = helpText;
    }

    return newObjective;
}

async function createProperty(name, tag, roster, year, gimmickMatches, championships, monthsRemaining = 12, storyName = null) {  // create_property
    // Set default values
    let activationValue = 1;
    let rating = null;
    let superstar = null;
    let superstarName = null;
    let teamSuperstars = [];
    let gimmickMatch = null;
    let gimmickMatchName = null;
    let championship = null;
    let championshipName = null;
    let activationText = null;
    let rosterSuperstars = (await getRosterSuperstars(roster)).filter(x => !x.retired && !x.dead && x.activeEffect !== 'traded'
        && x.activeEffect !== 'retirement_return' && !x.fullTimeManager);
    let availableRosterSuperstars = rosterSuperstars.filter(x => !x.injured || x.injured < 5);

    // Set property attributes
    if (tag === 'rating') {
        rating = myround(getRandomUniform(4.5, 5));
        activationValue = getRandomInt(year + 1, year + 2);

        if (name === 'gimmick matches') {
            rating = myround(getRandomUniform(4.5, 5));
            if (activationValue > 6) {
                activationValue = 6;
            }
            let availableGimmickMatches = gimmickMatches.filter(x => x.rules !== 'battle_royal' && x.available);
            if (availableGimmickMatches.length === 0) {
                // If no available gimmick matches
                return createProperty('matches', tag, roster, year, gimmickMatches, championships);
            }
            gimmickMatch = getRandomChoice(availableGimmickMatches);
        }
        else if (name === 'events') {
            rating = myround(getRandomUniform(4.5, 5));
            activationValue = getRandomInt(year + 1, year + 2);
            if (activationValue > 8) {
                activationValue = 8;
            }
            // Reduce events needed to complete goal if they are less than months remaining
            if (activationValue > monthsRemaining) {
                activationValue = monthsRemaining;
            }
        }
        else if (name === 'matches') {
            rating = myround(getRandomUniform(4.5, 5));
            activationValue = getRandomInt(year + 2, (year + 2) * 2);
            if (activationValue > 20) {
                activationValue = 20;
            }
        }
    }
    else if (tag === 'company') {
        activationValue = year + 2;
        if (activationValue > 10) {
            activationValue = 10;
        }

        if (name === 'new championship') {
            activationValue = 1;

        } else if (name === 'main eventers signed') {
            activationValue = year + 1;
            if (activationValue > 3) {
                activationValue = 3;
            }

        } else if (['win moty', 'win eoty', 'win woty', 'win gmoty'].includes(name)) {
            activationValue = 1;

        } else if (name === 'win awards') {
            activationValue = year + 1;
            if (activationValue > 5) {
                activationValue = 5;
            }

        } else if (name === 'championship prestige') {
            // Get championships that don't have maximum prestige
            let prestigeItems = Object.entries(championshipPrestigeXps);
            let maximumChampionshipPrestige = getArrayMaxByProperty(prestigeItems, 1)[0];
            let availableChampionships = championships.filter(x => x.prestige !== maximumChampionshipPrestige &&
                !x.matchContract && !x.championshipContract);
            if (availableChampionships.length === 0) {
                return createProperty('win awards', tag, roster, year, gimmickMatches, championships);
            }
            championship = getRandomChoice(availableChampionships);

            // Get next prestige
            activationText = prestigeItems.reverse().find(y => y[1] > championship.xp);
            if (activationText) {
                activationText = activationText[0];
            }
            activationValue = 1;
        }
    }
    else if (tag === 'superstar') {
        if (name === 'win battle royal') {
            let superstars = availableRosterSuperstars.filter(x => x.partTimer);
            if (superstars.length === 0) {
                superstar = getRandomChoice(rosterSuperstars);
            } else {
                superstar = getRandomChoice(superstars);
            }
        } else if (name === 'win championship contract') {
            let championshipContracts = gimmickMatches.filter(x => x.cashinContractName !== null);
            if (championshipContracts.length > 0) {
                let superstars = availableRosterSuperstars.filter(x => getRankFromPopularity(x.popularity) === 'Upper Midcarder');
                if (superstars.length === 0) {
                    superstar = getRandomChoice(rosterSuperstars);
                } else {
                    superstar = getRandomChoice(superstars);
                }
            } else {
                return createProperty('win battle royal', tag, roster, year, gimmickMatches, championships);
            }
        } else if (name === 'face of the company') {
            let superstars = availableRosterSuperstars
                .filter(x => getRankFromPopularity(x.popularity) === 'Main Eventer' && !x.faceOfTheCompany);
            if (superstars.length === 0) {
                superstars = availableRosterSuperstars.filter(x => !x.faceOfTheCompany);
                if (superstars.length === 0) {
                    return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
                } else {
                    superstar = getRandomChoice(superstars);
                }
            } else {
                superstar = getRandomChoice(superstars);
            }
        } else if (name === 'main eventer') {
            let superstars = availableRosterSuperstars
                .filter(x => getRankFromPopularity(x.popularity) === 'Upper Midcarder' && x.popularity <= 750);
            if (superstars.length === 0) {
                superstars = availableRosterSuperstars.filter(x => getRankFromPopularity(x.popularity) !== 'Main Eventer');
                if (superstars.length === 0) {
                    return createProperty('win battle royal', tag, roster, year, gimmickMatches, championships);
                } else {
                    superstar = getRandomChoice(superstars);
                }
            } else {
                superstar = getRandomChoice(superstars);
            }
            if (storyName === 'tutorial') {
                superstar = availableRosterSuperstars.find(x => x.name === 'Johnny Knight');
            }
        } else if (name === 'new tag team') {
            let tagTeamSuperstars = (await Promise.all((await getRosterTagTeams(roster)).map(x => getTagTeamSuperstars(x)))).flat();
            let superstars = availableRosterSuperstars
                .filter(x => getRankFromPopularity(x.popularity) === 'Lower Midcarder' && !x.partTimer &&
                    !objectInArray(x, tagTeamSuperstars));
            if (superstars.length < 2) {
                return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
            }
            let firstSuperstar = getRandomChoice(superstars);
            teamSuperstars.push(firstSuperstar);
            let otherSuperstars = superstars.filter(x => x.gender === firstSuperstar.gender &&
                (!x.brandName || x.brandName === firstSuperstar.brandName) && !objectInArray(x, teamSuperstars));
            if (otherSuperstars.length === 0) {
                return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
            }
            let secondSuperstar = getRandomChoice(otherSuperstars);
            teamSuperstars.push(secondSuperstar);
        } else if (name === 'new trio') {
            let tagTeamSuperstars = (await Promise.all((await getRosterTagTeams(roster)).map(x => getTagTeamSuperstars(x)))).flat();
            let superstars = availableRosterSuperstars
                .filter(x => getRankFromPopularity(x.popularity) === 'Upper Midcarder' && !x.partTimer &&
                    !objectInArray(x, tagTeamSuperstars));
            if (superstars.length < 3) {
                return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
            }
            let firstSuperstar = getRandomChoice(superstars);
            teamSuperstars.push(firstSuperstar);
            let otherSuperstars = superstars.filter(x => x.gender === firstSuperstar.gender &&
                (!x.brandName || x.brandName === firstSuperstar.brandName) &&
                !objectInArray(x, teamSuperstars));
            if (otherSuperstars.length < 2) {
                return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
            }
            let secondSuperstar = getRandomChoice(otherSuperstars);
            otherSuperstars = otherSuperstars.filter(x => x.$loki !== secondSuperstar.$loki);
            teamSuperstars.push(secondSuperstar);
            let thirdSuperstar = getRandomChoice(otherSuperstars);
            teamSuperstars.push(thirdSuperstar);
        } else if (name === 'get over') {
            let superstars = availableRosterSuperstars.filter(x => x.overness <= 60);
            if (superstars.length === 0) {
                superstars = availableRosterSuperstars.filter(x => !x.hot);
                if (superstars.length === 0) {
                    return createProperty('main eventer', tag, roster, year, gimmickMatches, championships);
                } else {
                    superstar = getRandomChoice(superstars);
                }
            } else {
                superstar = getRandomChoice(superstars);
            }
        }

        activationValue = 1;
    }

    // Set names
    if (superstar) {
        superstarName = superstar.name;
    } else if (teamSuperstars.length > 0) {
        superstarName = commaSeparate(teamSuperstars.map(z => z.name).sort((a, b) => a.localeCompare(b)))
    }
    if (gimmickMatch) {
        gimmickMatchName = gimmickMatch.name;
    }
    if (championship) {
        championshipName = getChampionshipDisplayName(championship);
    }

    // Create property
    let objectiveProperty = Property(name, activationValue, tag, rating, superstarName, gimmickMatchName, championshipName);
    await Properties.insert(objectiveProperty);
    if (activationText) {
        objectiveProperty.activationText = activationText;
    }

    // Add property relationships
    if (superstar) {
        await addPropertySuperstars(objectiveProperty, [superstar])
    } else if (teamSuperstars.length > 0) {
        await addPropertySuperstars(objectiveProperty, teamSuperstars)
    }
    if (gimmickMatch) {
        await addPropertyGimmickMatches(objectiveProperty, [gimmickMatch])
    }
    if (championship) {
        await addPropertyGimmickMatches(objectiveProperty, [championship])
    }

    return objectiveProperty;
}

function getChampionshipPrestigeIconHtml(prestige) {  // get_championship_prestige_icon_html
    let iconClass = 'bronze';
    if (prestige === 'C') {
        iconClass = 'bronze';
    } else if (prestige === 'B') {
        iconClass = 'silver';
    } else if (prestige === 'A') {
        iconClass = 'gold-light';
    } else if (prestige === 'S') {
        iconClass = 'obsidian';
    }
    return `<i class="fa fa-star-o font-bold ${iconClass}"></i>`;
}

export {generateCompanySingleObjective, generateCompanyObjectives}